<template>
  <empty-layout :enable-scroll-event="true" :show-footer="false" background-color="#2B2926">
    <div class="flex flex-col min-h-screen bg-[#2B2926]">
      <Loading v-if="isLoading" />
      <!-- Back button positioned absolutely over the hero image -->
      <BackButton class="absolute top-0 left-0 px-5 md:px-6 lg:px-8 z-10" />
      <!-- Hidden, not unmounted, while loading: the loader floats over the page, so
           cached content would otherwise show (and take taps) beneath it. -->
      <div v-show="!isLoading">
        <!-- Hero + dates + tabs stay static; swap attrs reactively -->
        <div
          class="relative w-full service-hero-offset aspect-[4/3] md:aspect-[16/9]"
          :class="{
            'service-hero-offset-ios': isIos,
            'service-hero-offset-android': isAndroid,
          }"
        >
          <img
            class="object-cover w-full h-full"
            :src="currentService.thumbnailSrc"
            alt="Service Banner"
          />
          <div
            class="absolute bottom-0 left-0 w-full h-[120px] md:h-[160px] bg-gradient-to-b from-transparent via-[rgba(43,41,38,0.6)] to-[rgba(43,41,38,1)]"
          ></div>
        </div>
        <div
          class="px-5 md:px-6 lg:px-8 pt-[16px] md:pt-[24px] pb-[40px] md:pb-[60px] max-w-[1024px] mx-auto"
        >
          <div class="relative mt-[-30px] md:mt-[-40px]">
            <h1
              class="font-[Inter] text-[1.5rem] md:text-[2rem] lg:text-[2.5rem] leading-[2.5rem] md:leading-[3rem] lg:leading-[3.5rem] text-white font-black tracking-[-0.03em]"
            >
              {{ serviceDates }}
            </h1>
            <ServiceTabs class="mt-[16px]" />
            <!-- Only below-pills content fades on service tab change -->
            <div ref="serviceContent">
              <Announcements
                v-if="currentService.announcements.length > 0"
                class="mt-[40px]"
                :announcements="currentService.announcements"
                @navigate="navigate"
              />
              <ServiceReplayButton v-if="showServiceReplay" class="mt-[40px]" />
              <DownloadablesSlider
                v-if="currentService.downloadables.length > 0"
                class="py-[40px]"
                ref="service-highlights-page-downloadables"
                baseSwiperId="service-highlights-page-downloadables"
                :items="currentService.downloadables"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </empty-layout>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import Announcements from '@/components/ServiceHighlightsPage/Announcements.vue';
import DownloadablesSlider from '@/components/ServiceHighlightsPage/DownloadablesSlider.vue';
import ServiceReplayButton from '@/components/ServiceHighlightsPage/ServiceReplayButton.vue';
import ServiceTabs from '@/components/ServiceHighlightsPage/ServiceTabs.vue';
import { createAnimation } from '@ionic/vue';
import { openUrl } from '@/utils/browser-helper';
import Loading from '@/components/common/LoadingState.vue';
import BackButton from '@/components/common/BackButton.vue';
import { isIosNativeOnly, isAndroidNativeOnly } from '@/utils/platform-check';

export default {
  data() {
    return {
      isLoading: true,
    };
  },
  components: {
    ServiceTabs,
    Announcements,
    DownloadablesSlider,
    ServiceReplayButton,
    Loading,
    BackButton,
  },
  computed: {
    ...mapGetters('serviceHighlights', ['serviceDates', 'currentService', 'selectedServiceIndex']),
    ...mapState('serviceReplay', ['weekends']),
    ...mapState('profile', {
      pastoralStatus: state => state.role,
    }),
    showServiceReplay() {
      const weekends = Array.isArray(this.weekends) ? this.weekends : [];
      const hasReplays = weekends.some(weekend => weekend.hogc_one_replays?.length > 0);
      const shouldHide =
        this.pastoralStatus === 'V' ||
        this.pastoralStatus === 'N' ||
        !hasReplays ||
        !this.currentService?.serviceReplayId;
      return !shouldHide;
    },
  },
  watch: {
    async selectedServiceIndex(newVal, oldVal) {
      if (newVal === oldVal) return;
      const el = this.$refs.serviceContent;

      // Hide new content when it renders
      el.style.opacity = '0';

      // Wait for Vue to render new content
      await this.$nextTick();

      // Fade in new content
      await createAnimation().addElement(el).duration(250).fromTo('opacity', '0', '1').play();
    },
  },
  async ionViewWillEnter() {
    try {
      await Promise.all([
        this.$store.dispatch('serviceHighlights/fetchPage'),
        this.$store.dispatch('serviceReplay/fetchWeekends'),
        this.$store.dispatch('profile/fetchProfile'),
      ]);
      this.isLoading = false;
    } catch (error) {
      console.error('Error fetching service highlights:', error);
    }
  },
  methods: {
    isIos() {
      return isIosNativeOnly();
    },
    isAndroid() {
      return isAndroidNativeOnly();
    },
    async navigate(announcement) {
      if (!announcement.path) return;

      const announcementType = announcement.type;
      if (announcementType === 'inAppBrowserURL') {
        await openUrl(
          { url: announcement.path },
          {
            title: announcement.title || announcement.dataEventTracking,
            source: 'service-highlights',
            page: 'ServiceHighlights',
          },
        );
      } else if (announcementType === 'externalURL') {
        window.open(announcement.path, '_blank');
      } else if (announcementType === 'internalPage') {
        this.$router.push(announcement.path);
      }
    },
  },
};
</script>
<style scoped>
/* Base offset - web default */
.service-hero-offset {
  margin-top: 0px; /* Pull image up slightly for web */
}

/* iOS: Offset to account for back button + safe area */
.service-hero-offset-ios {
  margin-top: calc(44px - env(safe-area-inset-top, 0px));
}

/* Android: Pull image up to extend behind status bar and back button */
.service-hero-offset-android {
  margin-top: calc(-28px + env(safe-area-inset-top, 0px));
}
</style>
